import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import "../assets/styles/chat.css";

/* ================================================================
   AI-Powered ChatBot — Groq (Llama 3) Integration
   ================================================================
   Sends messages to Groq's API with a system prompt containing
   Ushan's full portfolio context. Falls back to local keyword
   matching if the API call fails.
   ================================================================ */

const GROQ_API_URL = "https://api.groq.com/openai/v1/chat/completions";
const GROQ_API_KEY = process.env.REACT_APP_GROQ_API_KEY;
const MODEL = "llama-3.3-70b-versatile";

/* ─── Rate Limiting Constants ─── */
const MAX_MESSAGES_PER_MINUTE = 5;
const RATE_LIMIT_WINDOW = 60000; // 60 seconds

/* ─── System Prompt (Ushan's full context) ─── */
const SYSTEM_PROMPT = `You are Ushan Mihiranga's personal AI assistant embedded in his portfolio website. Your job is to answer questions about Ushan in a friendly, professional, and enthusiastic tone. Use emojis sparingly but effectively. Keep answers concise (2-4 paragraphs max) unless the user asks for detail.

## About Ushan
- Full name: Ushan Mihiranga
- Location: 168/6 First Lane, Welivita Road, Malabe, Sri Lanka
- Phone: +94 760 673 035
- Email: ushanmihiranga2017@gmail.com
- GitHub: github.com/Ushan2001
- Portfolio: This website you're on right now

## Current Role
- Associate Software Engineer at Fonix Edu Pvt Ltd (Atlas Axillia Group) — July 2025 to Present
- Develops key frontend features for a digital education platform using React.js
- Integrates REST APIs and implements OpenAI models for intelligent content processing
- Fine-tunes Hugging Face models using Python for accurate handwritten text detection

## Previous Roles
- Associate Software Engineer at Fonix Software Solution — Feb 2025 to July 2025
  - Built admin panels and dashboards for Coach Donee, Korean Master, and Rotary using React.js and TypeScript
  - Developed LMS backend with Python, OpenAI, Hugging Face, and Groq AI
  - Developed Grading System integrating Google OCR, TestReacted OCR, Claude AI, and OpenAI

- Software Engineer Intern at Fonix Software Solution — Feb 2024 to Feb 2025
  - Developed frontend landing pages and admin/client UIs using React.js
  - Created backend APIs and mobile routes with Node.js and Express.js
  - Implemented authentication, route protection, and integrated PayHere & Direct Pay payment gateways
  - Integrated WebSocket for real-time multiplayer features in ExamHub

## Education
- BSc (Hons) in Information Technology — Sri Lanka Institute of Information Technology (SLIIT) — Graduated (2022 - 2026), specializing in Software Engineering (Fresh Graduate)
- G.C.E Advanced Level 2020 — St Aloysius College Rathnapura — Physical Science stream (C3)

## Technical Skills
- Languages: Java, Python, C/C++, JavaScript, TypeScript, PHP, Kotlin
- Frontend: React.js, Next.js, Angular, HTML5, CSS3, React Native
- Backend: Node.js, Express.js, .NET
- Databases: MongoDB, MySQL, PostgreSQL
- Cloud & DevOps: AWS, Azure, Docker, Git
- AI/ML: OpenAI API, Hugging Face, Groq AI, Google OCR, Claude AI

## Key Projects
- FEDES AI: Emotion detection tool for educators using AI/ML
- ExamHUB: Gamified education platform with real-time multiplayer (WebSocket)
- Health Care Management System: Complete healthcare solution
- School Management System: Built with MERN stack
- E-commerce Platforms: Multiple buying/selling solutions
- AI Chat Bots: Including this one!

## Certifications
AWS SimuLearn Series:
- Core Security Concepts, Databases in Practice, Auto-Healing & Scaling Applications
- Highly Available Web Applications, File Systems in the Cloud, Amazon S3 Introduction

Microsoft Azure Fundamentals:
- Azure Identity Access & Security, Relational Database Services
- Azure Load Balancer for Scalability & Resiliency, Blob Storage, VM Disk Management

Google Cloud & AI:
- Introduction to Generative AI, Introduction to Large Language Models
- Introduction to Responsible AI, Applying AI Principles with Google Cloud

Also certified in AI-Assisted Python Programming from Niravana Clouds Academy.

## Response Guidelines
- Always refer to Ushan in first person ("I", "my") as you're representing him
- If asked about CV/resume, mention they can download it from the website
- If asked for contact, provide phone, email, and GitHub
- If asked something outside Ushan's info, politely redirect to what you know
- Never make up information that's not provided above
- Keep responses warm, enthusiastic, and professional`;

/* ─── Markdown → HTML Parser ─── */
const parseMarkdown = (text) => {
  if (!text) return "";
  return text
    // Escape any raw < > that are NOT already HTML tags (to avoid double-escaping)
    // Bold: **text** or __text__
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    // Italic: *text* or _text_
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/(?<!\\)_(.+?)_/g, "<em>$1</em>")
    // Inline code: `code`
    .replace(/`([^`]+)`/g, "<code style='background:rgba(255,255,255,0.15);padding:1px 5px;border-radius:4px;font-family:monospace;font-size:0.9em'>$1</code>")
    // Unordered list items: lines starting with - or *
    .replace(/^[*-] (.+)/gm, "<li>$1</li>")
    // Ordered list items: lines starting with 1. 2. etc.
    .replace(/^\d+\. (.+)/gm, "<li>$1</li>")
    // Wrap consecutive <li> items in a <ul>
    .replace(/(<li>.*<\/li>)/gs, "<ul style='margin:6px 0 6px 16px;padding:0;list-style:disc'>$1</ul>")
    // Headings: ### ## #
    .replace(/^### (.+)/gm, "<strong>$1</strong>")
    .replace(/^## (.+)/gm, "<strong>$1</strong>")
    .replace(/^# (.+)/gm, "<strong>$1</strong>")
    // Line breaks: double newline → paragraph break, single newline → <br/>
    .replace(/\n\n/g, "<br/><br/>")
    .replace(/\n/g, "<br/>");
};

const AdvancedChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [messageTimestamps, setMessageTimestamps] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  /* ─── SVG Icons ─── */
  const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
    </svg>
  );
  const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
  const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
  const UserIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );
  const BotIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
  );
  const ThumbsUpIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
    </svg>
  );
  const ThumbsDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z" />
    </svg>
  );

  const suggestedQuestions = useMemo(() => [
    "Tell me about yourself",
    "What are your technical skills?",
    "Show me your projects",
    "What certifications do you have?",
    "Where do you work?",
    "What's your educational background?",
    "How can I contact you?",
    "What are your future goals?",
    "Tell me about your AI experience",
    "What's your tech stack?",
  ], []);

  const greetings = useMemo(() => [
    "Hey! I'm Ushan's AI assistant 👋 Powered by UM, I can answer any question about Ushan's skills, projects, experience, and more. What would you like to know?",
    "Hi there! 🚀 I'm Ushan's smart assistant. Ask me anything about his work, tech stack, certifications, or projects — I've got all the details!",
    "Hello! ✨ Welcome to Ushan's portfolio. I'm his AI-powered assistant. Feel free to ask me about his career, skills, or anything else!",
  ], []);

  /* ─── Call Groq API ─── */
  const callGroqAPI = useCallback(async (userMessage, history) => {
    if (!GROQ_API_KEY) {
      throw new Error("API key not configured");
    }

    const apiMessages = [
      { role: "system", content: SYSTEM_PROMPT },
      ...history.slice(-10), // Keep last 10 messages for context
      { role: "user", content: userMessage },
    ];

    const response = await fetch(GROQ_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${GROQ_API_KEY}`,
      },
      body: JSON.stringify({
        model: MODEL,
        messages: apiMessages,
        max_tokens: 1024,
        temperature: 0.7,
        top_p: 0.9,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData?.error?.message || `API error ${response.status}`);
    }

    const data = await response.json();
    return data.choices?.[0]?.message?.content || "I'm not sure how to answer that. Could you rephrase?";
  }, []);

  /* ─── Fallback: local keyword matching ─── */
  const fallbackResponse = useCallback((input) => {
    const lower = input.toLowerCase();
    const kb = {
      greeting: { kw: ["hi", "hello", "hey", "greetings"], res: "Hey! I'm Ushan 👋 A full-stack developer passionate about creating amazing web experiences. What would you like to know about me?" },
      skills: { kw: ["skills", "tech", "stack", "technologies"], res: "I work with <strong>React.js, Node.js, TypeScript, Python, MongoDB, MySQL, AWS, Docker</strong> and more! Ask me about any specific technology." },
      projects: { kw: ["projects", "built", "portfolio"], res: "I've built FEDES AI (emotion detection), ExamHUB (gamified education), Healthcare Management System, and many more! 🚀" },
      experience: { kw: ["work", "experience", "job", "career", "fonix"], res: "I'm currently an Associate Software Engineer at <strong>Fonix Edu Pvt Ltd (Atlas Axillia Group)</strong>. Previously interned and worked at Fonix Software Solution." },
      education: { kw: ["education", "university", "sliit", "study"], res: "I'm a fresh graduate with a <strong>BSc (Hons) in IT</strong> from SLIIT, specializing in Software Engineering! 🎓" },
      contact: { kw: ["contact", "email", "phone", "reach"], res: "📧 ushanmihiranga2017@gmail.com<br/>📞 +94 760 673 035<br/>💻 github.com/Ushan2001" },
    };

    for (const [, v] of Object.entries(kb)) {
      if (v.kw.some((k) => lower.includes(k))) return v.res;
    }
    return "I'd love to help! Try asking about my skills, projects, experience, or education. 😊";
  }, []);

  /* ─── Rate Limiting Check ─── */
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const recentMessages = messageTimestamps.filter((timestamp) => now - timestamp < RATE_LIMIT_WINDOW);

    if (recentMessages.length >= MAX_MESSAGES_PER_MINUTE) {
      return false;
    }
    return true;
  }, [messageTimestamps]);

  /* ─── Scroll ─── */
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

  /* ─── Welcome message ─── */
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        role: "assistant",
        content: greetings[Math.floor(Math.random() * greetings.length)],
        timestamp: new Date(),
        category: "greeting",
      }]);
    }
  }, [isOpen, greetings, messages.length]);

  /* ─── Send message ─── */
  const handleSendMessage = useCallback(async (messageText = null) => {
    const text = (messageText || inputMessage).trim();
    if (!text || isLoading) return;

    // Check rate limiting
    if (!checkRateLimit()) {
      const warningMessage = {
        role: "assistant",
        content: "⚠️ You're sending messages too quickly! Please wait a moment before sending more messages.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, warningMessage]);
      return;
    }

    // Track message timestamp
    setMessageTimestamps((prev) => [...prev, Date.now()]);

    const userMessage = { role: "user", content: text, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    setIsTyping(true);
    setShowSuggestions(false);

    let responseContent;
    try {
      responseContent = await callGroqAPI(text, conversationHistory);
    } catch (err) {
      console.warn("Groq API failed, using fallback:", err.message);
      responseContent = fallbackResponse(text);
    }

    // Update conversation history for context
    setConversationHistory((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: responseContent },
    ]);

    setIsTyping(false);

    const botResponse = {
      role: "assistant",
      content: responseContent,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, botResponse]);
    setIsLoading(false);
  }, [inputMessage, isLoading, callGroqAPI, conversationHistory, fallbackResponse, checkRateLimit]);

  const handleSuggestionClick = (suggestion) => handleSendMessage(suggestion);

  const handleFeedback = (messageIndex, isPositive) => {
    setMessages((prev) =>
      prev.map((msg, idx) =>
        idx === messageIndex ? { ...msg, feedback: isPositive ? "positive" : "negative" } : msg
      )
    );
  };

  const formatTime = (date) => date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="chatbot-container">
      {!isOpen && (
        <button onClick={() => setIsOpen(true)} className="chat-button">
          <ChatIcon />
        </button>
      )}

      {isOpen && (
        <div className="chat-window">
          {/* Liquid Glass Background Orbs */}
          <div className="chat-liquid-bg">
            <div className="liquid-orb orb-1"></div>
            <div className="liquid-orb orb-2"></div>
            <div className="liquid-orb orb-3"></div>
          </div>

          <div className="chat-header">
            <div className="header-content">
              <div className="avatar-container">🤖</div>
              <div>
                <h3 className="header-title">Ushan's AI Assistant</h3>
                <p className="header-subtitle">Powered by UM · Ask me anything!</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="close-button">
              <CloseIcon />
            </button>
          </div>

          <div className="messages-container">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`message-wrapper ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`message-content ${message.role === "user" ? "user-message" : "bot-message"}`}>
                  {message.role === "assistant" && (
                    <div className="message-meta">
                      <BotIcon />
                      <span className="meta-text">AI Assistant</span>
                      <span className="meta-text">🕐</span>
                      <span className="meta-text">{formatTime(message.timestamp)}</span>
                    </div>
                  )}

                  <div className="message-body">
                    {message.role === "user" && (
                      <div className="message-meta user-meta">
                        <UserIcon />
                        <span className="meta-text">{formatTime(message.timestamp)}</span>
                      </div>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: parseMarkdown(message.content) }} />
                  </div>

                  {message.role === "assistant" && !message.feedback && index > 0 && (
                    <div className="feedback-buttons">
                      <button onClick={() => handleFeedback(index, true)} className="feedback-button thumbs-up">
                        <ThumbsUpIcon />
                      </button>
                      <button onClick={() => handleFeedback(index, false)} className="feedback-button thumbs-down">
                        <ThumbsDownIcon />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="message-wrapper justify-start">
                <div className="message-body bot-message">
                  <div className="typing-indicator">
                    <div className="dot"></div>
                    <div className="dot dot-delay-1"></div>
                    <div className="dot dot-delay-2"></div>
                  </div>
                </div>
              </div>
            )}

            {showSuggestions && messages.length <= 1 && (
              <div className="suggestions-container">
                <p className="suggestions-title">Quick questions:</p>
                <div className="suggestions-list">
                  {suggestedQuestions.map((question, index) => (
                    <button key={index} onClick={() => handleSuggestionClick(question)} className="suggestion-button">
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="input-container">
            <div className="input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask about skills, projects, experience..."
                className="chat-input"
                disabled={isLoading}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={isLoading || !inputMessage.trim()}
                className="send-button"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedChatBot;
